import { ref, computed } from 'vue';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { App } from '@capacitor/app';
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings';
import { SUPPORTED_LANGUAGES } from '@/config/languages';
import { useUserStore } from '@/stores/user';
import { useSettingsUpdate } from '@/composables/useSettingsUpdate';
import i18n, { setLocale } from '@/i18n';

// Module-level listener reference for cleanup
let appStateListener = null;

/**
 * Get the current app language code
 * Used for API calls that need lang parameter
 * @returns {string} Current locale code (e.g., 'en', 'de')
 */
export const getCurrentLanguageCode = () => i18n.global.locale.value;

/**
 * Get the device's current language code
 * For per-app language settings (iOS 13+, Android 13+), this returns the per-app language
 * @returns {Promise<string|null>} Language code (e.g., 'en', 'de') or null
 */
export const getDeviceLanguageCode = async () => {
  try {
    const result = await Device.getLanguageCode();
    // Returns { value: 'en-US' } or { value: 'en' }
    const languageTag = result.value;

    // Extract the primary language code (e.g., 'en' from 'en-US')
    const primaryCode = languageTag.split('-')[0].toLowerCase();

    return primaryCode;
  } catch (error) {
    console.error('Failed to get device language code:', error);

    return null;
  }
};

/**
 * Check if a language code is supported
 * @param {string} code - Language code
 * @returns {boolean}
 */
export const isSupportedLanguage = (code) => {
  return SUPPORTED_LANGUAGES.some((lang) => lang.code === code);
};

/**
 * Sync the system/device language to vue-i18n and backend
 * Called on app startup and when app resumes from background
 * - Native (iOS/Android): Syncs device's per-app language setting
 * - Web: Applies stored language from user settings
 * @returns {Promise<void>}
 */
export const syncSystemLocale = async () => {
  const userStore = useUserStore();
  const currentStoredLang = userStore.settings.language || 'en';

  // On web, simply apply the stored language preference
  if (!Capacitor.isNativePlatform()) {
    if (isSupportedLanguage(currentStoredLang)) {
      console.log(`🌐 Web: Applying stored language: ${currentStoredLang}`);
      await setLocale(currentStoredLang);
    }

    return;
  }

  // Native platforms: sync device language to app
  const { handleSettingChange } = useSettingsUpdate();

  const deviceLang = await getDeviceLanguageCode();

  if (!deviceLang) {
    console.log('📱 Could not detect device language');

    return;
  }

  // Check if detected language is supported
  if (!isSupportedLanguage(deviceLang)) {
    console.log(`📱 Device language "${deviceLang}" is not supported, using fallback`);

    return;
  }

  // If device language matches stored language, just ensure vue-i18n is in sync
  if (deviceLang === currentStoredLang) {
    console.log(`📱 Language already synced: ${deviceLang}`);
    await setLocale(deviceLang);

    return;
  }

  // Device language is different from stored - sync it
  console.log(`📱 Syncing language: ${currentStoredLang} → ${deviceLang}`);

  // Update vue-i18n locale
  await setLocale(deviceLang);

  // Sync to backend
  await handleSettingChange('language', deviceLang, currentStoredLang);

  console.log(`✅ Language synced to: ${deviceLang}`);
};

/**
 * Initialize language sync listener for app resume events
 * Call this once on app startup (in App.vue)
 */
export const initLanguageSyncListener = () => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  // Clean up existing listener if any
  if (appStateListener) {
    appStateListener.remove();
    appStateListener = null;
  }

  // Listen for app state changes (resume from background)
  App.addListener('appStateChange', async ({ isActive }) => {
    if (isActive) {
      console.log('📱 App resumed - checking language sync...');
      await syncSystemLocale();
    }
  }).then((listener) => {
    appStateListener = listener;
  });
};

/**
 * Clean up language sync listener
 * Call this on app unmount if needed
 */
export const removeLanguageSyncListener = () => {
  if (appStateListener) {
    appStateListener.remove();
    appStateListener = null;
  }
};

export function useLanguageSettings() {
  const userStore = useUserStore();
  const platformInfo = ref(null);

  /**
   * Current language code from user settings
   */
  const currentLanguage = computed(() => userStore.settings.language || 'en');

  /**
   * Check if platform requires system settings redirect
   * - iOS: Always uses system settings
   * - Android 13+ (API 33+): Uses system per-app language settings
   * - Android <13 / Web: Uses in-app picker
   */
  const requiresSystemSettings = computed(() => {
    if (!platformInfo.value) {
      return false;
    }

    const { platform, osVersion } = platformInfo.value;

    // iOS always uses system settings
    if (platform === 'ios') {
      return true;
    }

    // Android 13+ (API 33+) uses system per-app language settings
    if (platform === 'android' && osVersion) {
      const majorVersion = parseInt(osVersion.split('.')[0], 10);

      return majorVersion >= 13;
    }

    // Web and older Android use in-app picker
    return false;
  });

  /**
   * Initialize platform info
   * Call this on component mount
   */
  const initPlatformInfo = async () => {
    const platform = Capacitor.getPlatform();

    if (platform === 'web') {
      platformInfo.value = { platform: 'web', osVersion: '' };

      return;
    }

    try {
      const info = await Device.getInfo();
      platformInfo.value = {
        platform: info.platform,
        osVersion: info.osVersion,
      };
    } catch (error) {
      console.error('Failed to get device info:', error);
      platformInfo.value = { platform, osVersion: '' };
    }
  };

  /**
   * Open system app settings for language
   */
  const openSystemLanguageSettings = async () => {
    await NativeSettings.open({
      optionAndroid: AndroidSettings.ApplicationDetails,
      optionIOS: IOSSettings.App,
    });
  };

  /**
   * Get all supported languages for picker
   */
  const getSupportedLanguages = () => SUPPORTED_LANGUAGES;

  return {
    currentLanguage,
    requiresSystemSettings,
    platformInfo,
    initPlatformInfo,
    openSystemLanguageSettings,
    getSupportedLanguages,
    syncSystemLocale,
  };
}
